/* =============================================================
   Nine Two Five Silver — send-a-piece.js · v1 · 13 October 2026
   The POS tool screen at /pages/send-a-piece (#318). Anna finds the
   piece and its size, hands the phone over, the customer types their
   email, and the wishlist worker (POST /apps/wishlist/send) puts the
   piece on their wishlist and emails it to them once.
   Steps: find -> size (sized pieces only) -> handover -> confirm -> done.
   The tile token comes from the fragment (#k=<token>): an inline script
   in layout/theme.liquid lifts it into window.ntfSendToken and strips
   the fragment before Clarity and the pixels load; it is read once here,
   deleted, and only ever lives in this closure. Nothing about the customer is stored in the
   browser; the done step clears itself after 15 seconds. No dependencies.
   ============================================================= */
(function () {
  'use strict';

  var root = document.querySelector('[data-send-page]');
  if (!root) return;

  var PROXY = root.getAttribute('data-send-proxy') || '/apps/wishlist/send';
  var MAX_PIECES = 5; // pieces in one email (#417)
  var DONE_RESET_MS = 15000;
  var SEARCH_DEBOUNCE_MS = 200;
  var SEND_TIMEOUT_MS = 20000;
  // Shown with no token (the page was reloaded or typed in, so the fragment is gone) and on a 401 (wrong token).
  var NO_TOKEN = 'Open this screen from the Send a piece tile. If it still says this, ask Andy.';

  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };

  // The tile token: lifted out of #k=... by the inline script in layout/theme.liquid (which also strips the
  // fragment so Clarity and the pixels never see it), read once here, kept in this closure only. Never stored,
  // never logged. The fragment is still checked in case the inline script did not run.
  var token = (function () {
    var t = '';
    if (typeof window.ntfSendToken === 'string') t = window.ntfSendToken;
    try { delete window.ntfSendToken; } catch (e) { window.ntfSendToken = undefined; }
    if (t) return t;
    var m = /(?:^#|&)k=([^&]+)/.exec(location.hash || '');
    if (!m) return '';
    try { t = decodeURIComponent(m[1]); } catch (e) { return ''; }
    try { history.replaceState(history.state, '', location.pathname + location.search); } catch (e) { /* ignore */ }
    return t;
  })();

  var els = {
    search: $('[data-send-search]'),
    results: $('[data-send-results]'),
    hint: $('[data-send-search-hint]'),
    categories: $('[data-send-categories]'),
    back: $('[data-send-back]'),
    picked: $('[data-send-picked]'),
    pickedList: $('[data-send-picked-list]'),
    sizes: $('[data-send-sizes]'),
    pieceSmall: $('[data-send-piece-small]'),
    handoverTitle: $('[data-send-handover-title]'),
    piece: $('[data-send-piece]'),
    addAnother: $('[data-send-add]'),
    form: $('[data-send-form]'),
    email: $('[data-send-email]'),
    consent: $('[data-send-consent]'),
    confirmEmail: $('[data-send-confirm-email]'),
    confirmPieces: $('[data-send-confirm-pieces]'),
    yes: $('[data-send-yes]'),
    fix: $('[data-send-fix]'),
    doneTitle: $('[data-send-done-title]'),
    doneText: $('[data-send-done-text]'),
    donePieces: $('[data-send-done-pieces]'),
    next: $('[data-send-next]'),
    status: $('[data-send-status]')
  };

  // A category list with more than one product type is grouped by type in this order, each under a short heading
  // (#419). Any other type follows, alphabetically, under its own name.
  var TYPE_ORDER = ['Gift voucher', 'Sterling silver earring', 'Sterling silver ring', 'Sterling silver pendant', 'Sterling silver bangle', 'Accessories'];
  var TYPE_LABELS = {
    'Gift voucher': 'Gift voucher',
    'Sterling silver earring': 'Earrings',
    'Sterling silver ring': 'Rings',
    'Sterling silver pendant': 'Pendants',
    'Sterling silver bangle': 'Bangles',
    'Accessories': 'Accessories'
  };

  // Everything about the current customer and pieces. reset() empties it.
  // pieces: the picked pieces, in the order picked, each {handle, variantId, sizeTitle, title, imageUrl, price}.
  // current: the product being picked now (between Find and the size), {handle, title, image, price, multi}.
  var state = { step: 'find', pieces: [], current: null, email: '', sending: false };
  var searchTimer = null, searchSeq = 0, doneTimer = null;
  // True while a category list is open (or loading) on the Find step: Back then returns to the categories.
  var browsing = false;
  // Bumped by reset() and by each pick: a product or send reply that arrives after either is dropped.
  var epoch = 0;

  /* ------- Analytics (same mechanism as wishlist.js: Shopify custom events, picked up by GA4) ------- */
  function publish(name, payload) {
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, payload); } catch (e) { /* ignore */ }
  }

  function track(outcome) {
    var hasVariant = state.pieces.some(function (p) { return !!p.variantId; });
    publish('ntf:send_piece', { outcome: outcome, consent: !!(els.consent && els.consent.checked), has_variant: hasVariant, pieces: state.pieces.length });
  }

  /* ------- Steps and history ------- */
  function setStatus(text) { if (els.status) els.status.textContent = text || ''; }

  // The first control of a step gets focus; steps with no control first focus their heading.
  function focusStep(step) {
    var box = $('[data-send-step="' + step + '"]');
    if (!box) return;
    var target = step === 'find' ? els.search
      : step === 'size' ? $('.send-piece__size', box)
      : step === 'handover' ? els.email
      : step === 'confirm' ? els.yes
      : step === 'done' ? els.next
      : null;
    (target || $('h1', box)).focus();
  }

  // Each history entry remembers how many pieces were picked when it was made (n), so Back past a
  // hand-over drops the piece that hand-over added.
  function remember(mode, step) {
    try {
      var entry = { ntfSend: step, n: state.pieces.length };
      if (mode === 'push') history.pushState(entry, '');
      else if (mode !== 'none') history.replaceState(entry, '');
    } catch (e) { /* history unavailable: steps still work */ }
  }

  // Each step is a history entry, so the phone's Back gesture steps back through the tool. Back is
  // never allowed to land on the confirm or done step: those show the customer's address.
  function show(step, opts) {
    opts = opts || {};
    state.step = step;
    $$('[data-send-step]').forEach(function (box) { box.hidden = box.getAttribute('data-send-step') !== step; });
    setStatus('');
    clearTimeout(doneTimer);
    if (step === 'done') doneTimer = setTimeout(reset, DONE_RESET_MS);
    if (step === 'find') renderPicked();
    if (step === 'handover') renderHandover();
    remember(opts.history || 'replace', step);
    window.scrollTo(0, 0);
    focusStep(step);
  }

  window.addEventListener('popstate', function (e) {
    var target = e.state && e.state.ntfSend;
    var n = e.state && typeof e.state.n === 'number' ? e.state.n : 0;
    // Leaving confirm or done, or arriving at a step whose data is gone: start again from a clean page.
    if (state.step === 'done' || state.step === 'confirm' && target !== 'handover' || !target || target === 'confirm' || target === 'done' ||
        (target !== 'find' && !state.pieces.length && !state.current)) {
      reset('none');
      return;
    }
    // Back past a hand-over: that hand-over's piece comes off the list.
    if (n < state.pieces.length) state.pieces.length = n;
    if (target === 'find') { clearCurrent(); show('find', { history: 'none' }); return; }
    if (target === 'size' && !(state.current && state.current.multi)) {
      clearCurrent();
      show(state.pieces.length ? 'handover' : 'find', { history: 'replace' });
      return;
    }
    if (target === 'handover' && !state.pieces.length) { show('find', { history: 'replace' }); return; }
    show(target, { history: 'none' });
  });

  // Back to the page from the browser's cache (or a reload that kept the DOM): clear it.
  window.addEventListener('pageshow', function (e) { if (e.persisted) reset(); });
  window.addEventListener('pagehide', function () { wipeCustomer(); });

  /* ------- Reset ------- */
  function wipeCustomer() {
    state.email = '';
    if (els.email) els.email.value = '';
    if (els.consent) els.consent.checked = false;
    if (els.confirmEmail) els.confirmEmail.textContent = '';
    if (els.doneText) els.doneText.textContent = '';
  }

  // The product being picked now (not the pieces already picked).
  function clearCurrent() {
    state.current = null;
    if (els.sizes) els.sizes.innerHTML = '';
    if (els.pieceSmall) els.pieceSmall.innerHTML = '';
  }

  function clearPieces() {
    state.pieces = [];
    clearCurrent();
    if (els.piece) els.piece.innerHTML = '';
    if (els.pickedList) els.pickedList.innerHTML = '';
    if (els.confirmPieces) els.confirmPieces.innerHTML = '';
    if (els.donePieces) els.donePieces.innerHTML = '';
  }

  // The Find step as it first looks: empty search, the category buttons, no results.
  function clearFind() {
    clearTimeout(searchTimer);
    searchSeq++;
    if (els.search) els.search.value = '';
    showCategories();
  }

  function reset(historyMode) {
    clearTimeout(doneTimer);
    epoch++;
    wipeCustomer();
    try { sessionStorage.clear(); } catch (e) { /* blocked */ }
    clearPieces();
    state.sending = false;
    setBusy(false);
    clearFind();
    show('find', { history: typeof historyMode === 'string' ? historyMode : 'replace' });
    // Opened without the tile's #k= token: say so now rather than after the customer has typed.
    if (!token) setStatus(NO_TOKEN);
  }

  /* ------- Helpers ------- */
  function money(str) {
    var n = parseFloat(str);
    return isNaN(n) ? '' : '£' + n.toFixed(2);
  }

  function priceText(p) {
    if (p.price_max && p.price_min && parseFloat(p.price_max) > parseFloat(p.price_min)) return 'From ' + money(p.price_min);
    return money(p.price);
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function img(url, width, alt) {
    var el = document.createElement('img');
    if (url) el.src = sizedImage(url, width);
    el.alt = alt || '';
    el.width = width / 2; el.height = width / 2;
    el.loading = 'lazy';
    el.decoding = 'async';
    return el;
  }

  function el(tag, className, text) {
    var e = document.createElement(tag);
    if (className) e.className = className;
    if (text) e.textContent = text;
    return e;
  }

  // One piece card: picture, title, size line and price. The size step, hand-over, confirm and done steps all use it.
  function pieceCard(p, width) {
    var box = el('div', 'send-piece__piece');
    box.appendChild(img(p.imageUrl, width, ''));
    var text = el('div');
    text.appendChild(el('div', 'send-piece__piece-title', p.title));
    if (p.sizeTitle) text.appendChild(el('div', 'send-piece__piece-meta', 'Size ' + p.sizeTitle));
    if (p.price) text.appendChild(el('div', 'send-piece__piece-meta', p.price));
    box.appendChild(text);
    return box;
  }

  // Every picked piece as a card: large when there is one, compact when there are more (hand-over, confirm, done).
  function renderPieces(box) {
    if (!box) return;
    box.innerHTML = '';
    var compact = state.pieces.length > 1;
    box.classList.toggle('send-piece__pieces--compact', compact);
    state.pieces.forEach(function (p) { box.appendChild(pieceCard(p, compact ? 144 : 240)); });
  }

  // The hand-over step: its title follows the piece count, then every picked piece.
  function renderHandover() {
    if (els.handoverTitle) els.handoverTitle.textContent = state.pieces.length > 1 ? 'Send these pieces' : 'Send this piece';
    renderPieces(els.piece);
    if (els.addAnother) els.addAnother.hidden = state.pieces.length >= MAX_PIECES;
  }

  // Back on the search line: shown while a category list or the picked-so-far list is open, otherwise hidden
  // (and the search box takes the whole line).
  function updateBack() {
    if (els.back) els.back.hidden = !(browsing || state.pieces.length);
  }

  // The Find step's short list of pieces already picked, each with a remove cross.
  function renderPicked() {
    updateBack();
    if (!els.picked || !els.pickedList) return;
    els.pickedList.innerHTML = '';
    els.picked.hidden = !state.pieces.length;
    state.pieces.forEach(function (p, i) {
      var name = p.title + (p.sizeTitle ? ', size ' + p.sizeTitle : '');
      var li = el('li', 'send-piece__picked-item');
      li.appendChild(el('span', 'send-piece__picked-name', name));
      var x = el('button', 'send-piece__remove', '×');
      x.type = 'button';
      x.setAttribute('aria-label', 'Remove ' + name);
      x.addEventListener('click', function () {
        state.pieces.splice(i, 1);
        renderPicked();
        remember('replace', 'find');
        if (els.search) els.search.focus();
      });
      li.appendChild(x);
      els.pickedList.appendChild(li);
    });
  }

  /* ------- Find: search ------- */
  function search(q) {
    var seq = ++searchSeq;
    if (!q || q.length < 2) {
      showCategories();
      return;
    }
    hideCategories(false);
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
      .then(function (data) {
        if (seq !== searchSeq) return;
        var r = (data && data.resources && data.resources.results) || {};
        // Point of Sale listings have no online page (no url): they cannot go on a wishlist.
        var products = (r.products || []).filter(function (p) { return p && p.url && p.handle; }).map(function (p) {
          return { handle: p.handle, title: p.title, image: p.image || (p.featured_image && p.featured_image.url) || '', price: priceText(p) };
        });
        renderResults(products, 'Nothing on the website matches “' + q + '”.');
      })
      .catch(function () {
        if (seq !== searchSeq) return;
        els.results.innerHTML = '';
        els.hint.hidden = false;
        els.hint.textContent = 'No signal. Try again';
      });
  }

  /* ------- Find: browse by category ------- */
  // The category buttons are the section's blocks (sections/main-send-a-piece.liquid), one per collection.
  function showCategories() {
    browsing = false;
    updateBack();
    if (els.categories) els.categories.hidden = false;
    if (els.results) els.results.innerHTML = '';
    // No hint at rest: the box's placeholder says what to type (#419, compact screens).
    if (els.hint) { els.hint.hidden = true; els.hint.textContent = ''; }
  }

  function hideCategories(isBrowsing) {
    browsing = !!isBrowsing;
    updateBack();
    if (els.categories) els.categories.hidden = true;
  }

  function byTitle(a, b) {
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });
  }

  // Where a product type sits in a grouped list: the known types in TYPE_ORDER, then the rest alphabetically
  // (blank type last).
  function compareTypes(a, b) {
    var ia = TYPE_ORDER.indexOf(a), ib = TYPE_ORDER.indexOf(b);
    if (ia > -1 || ib > -1) return (ia > -1 ? ia : TYPE_ORDER.length) - (ib > -1 ? ib : TYPE_ORDER.length);
    if (!a !== !b) return a ? -1 : 1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  }

  // A category list: alphabetical by title. When it holds more than one product type (Everything), grouped by
  // type first, with a heading entry ({heading}) before each group.
  function arrange(products) {
    var types = [];
    products.forEach(function (p) { if (types.indexOf(p.type) < 0) types.push(p.type); });
    if (types.length < 2) return products.slice().sort(byTitle);
    var out = [];
    types.sort(compareTypes).forEach(function (t) {
      out.push({ heading: TYPE_LABELS[t] || t || 'Other' });
      products.filter(function (p) { return p.type === t; }).sort(byTitle).forEach(function (p) { out.push(p); });
    });
    return out;
  }

  // Collection products.json: only pieces on the Online Store, so every one can go on a wishlist.
  function browse(handle, label) {
    var seq = ++searchSeq;
    clearTimeout(searchTimer);
    if (els.search) els.search.value = '';
    hideCategories(true);
    els.results.innerHTML = '';
    els.hint.hidden = false;
    els.hint.textContent = 'Loading ' + label + '…';
    fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
      .then(function (data) {
        if (seq !== searchSeq) return;
        var products = ((data && data.products) || []).filter(function (p) { return p && p.handle; }).map(function (p) {
          var prices = (p.variants || []).map(function (v) { return parseFloat(v.price); }).filter(function (n) { return !isNaN(n); });
          var min = prices.length ? Math.min.apply(null, prices) : NaN;
          var max = prices.length ? Math.max.apply(null, prices) : NaN;
          return {
            handle: p.handle,
            title: p.title,
            type: String(p.product_type || '').trim(),
            image: (p.images && p.images[0] && p.images[0].src) || '',
            price: isNaN(min) ? '' : (max > min ? 'From ' : '') + money(min)
          };
        });
        renderResults(arrange(products), 'Nothing in ' + label + ' on the website.');
        if (els.back) els.back.focus();
      })
      .catch(function (err) {
        if (seq !== searchSeq) return;
        els.results.innerHTML = '';
        els.hint.hidden = false;
        els.hint.textContent = err === 404 ? 'That category isn’t on the website' : 'No signal. Try again';
      });
  }

  $$('[data-send-category]').forEach(function (b) {
    b.addEventListener('click', function () {
      var handle = b.getAttribute('data-send-category');
      if (handle) browse(handle, (b.textContent || '').trim());
    });
  });

  // Back on the search line: from a category list to the categories; from the picked-so-far list to the hand-over.
  if (els.back) els.back.addEventListener('click', function () {
    if (browsing) {
      searchSeq++;
      showCategories();
      var first = $('[data-send-category]');
      if (first) first.focus();
      return;
    }
    if (!state.pieces.length) return;
    clearTimeout(searchTimer);
    searchSeq++;
    clearCurrent();
    goHandover();
  });

  // Search results and a category's pieces share one list and one pick.
  function renderResults(products, emptyText) {
    els.results.innerHTML = '';
    if (!products.length) {
      els.hint.hidden = false;
      els.hint.textContent = emptyText;
      return;
    }
    els.hint.hidden = true;
    products.forEach(function (p) {
      if (p.heading) { els.results.appendChild(el('p', 'send-piece__group-head', p.heading)); return; }
      var b = el('button', 'send-piece__row');
      b.type = 'button';
      var thumb = img(p.image, 144, '');
      thumb.className = 'send-piece__thumb';
      var text = el('span', 'send-piece__row-text');
      text.appendChild(el('span', 'send-piece__row-title', p.title));
      text.appendChild(el('span', 'send-piece__row-price', p.price));
      b.appendChild(thumb); b.appendChild(text);
      b.addEventListener('click', function () { choose({ handle: p.handle, title: p.title, image: p.image, price: p.price }, b); });
      els.results.appendChild(b);
    });
  }

  /* ------- Size ------- */
  function choose(piece, button) {
    if (state.pieces.length >= MAX_PIECES) { setStatus('Five pieces is the most for one email'); return; }
    var at = ++epoch;
    if (button) button.disabled = true;
    setStatus('');
    fetch('/products/' + encodeURIComponent(piece.handle) + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
      .then(function (product) {
        if (button) button.disabled = false;
        if (at !== epoch) return;
        var variants = (product && Array.isArray(product.variants)) ? product.variants : [];
        state.current = piece;
        state.current.multi = variants.length > 1;
        if (!state.current.multi) { pick(null, '', variants.length ? variantPrice(variants[0]) : ''); return; }
        renderSizes(variants);
        if (els.pieceSmall) {
          els.pieceSmall.innerHTML = '';
          els.pieceSmall.appendChild(pieceCard({ title: piece.title, imageUrl: piece.image, price: piece.price, sizeTitle: '' }, 240));
        }
        show('size', { history: 'push' });
      })
      .catch(function (err) {
        if (button) button.disabled = false;
        if (at !== epoch) return;
        setStatus(err === 404 ? 'This piece isn’t on the website' : 'No signal. Try again');
      });
  }

  function renderSizes(variants) {
    els.sizes.innerHTML = '';
    // The shop's size order is the variant order.
    variants.forEach(function (v) {
      els.sizes.appendChild(sizeButton(v.title, String(v.id), variantPrice(v)));
    });
    // No size yet: the product's price as the list showed it ("From £x" when the sizes differ).
    var none = sizeButton('No size yet', null, '');
    none.classList.add('send-piece__size--none');
    els.sizes.appendChild(none);
  }

  // A variant's price from /products/<handle>.js, which gives it in pence.
  function variantPrice(v) {
    var pence = v ? parseFloat(v.price) : NaN;
    return isNaN(pence) ? '' : money(pence / 100);
  }

  function sizeButton(label, variantId, price) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'send-piece__size';
    b.textContent = label;
    b.addEventListener('click', function () { pick(variantId, variantId ? label : '', price); });
    return b;
  }

  // The current product (and size) goes on the list; the same piece in the same size is not added twice.
  // price: the picked variant's price, or '' to keep the price the list showed.
  function pick(variantId, sizeTitle, price) {
    var c = state.current;
    if (!c) return;
    var already = state.pieces.some(function (p) { return p.handle === c.handle && p.variantId === variantId; });
    if (!already) {
      state.pieces.push({ handle: c.handle, variantId: variantId, sizeTitle: sizeTitle, title: c.title, imageUrl: c.image, price: price || c.price });
    }
    goHandover();
  }

  function goHandover() {
    wipeCustomer();
    show('handover', { history: 'push' });
  }

  /* ------- Hand-over and confirm ------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  els.form.addEventListener('submit', function (e) {
    e.preventDefault();
    var email = (els.email.value || '').trim();
    if (!email || email.length > 254 || !EMAIL_RE.test(email)) {
      setStatus('Check the email address');
      els.email.focus();
      return;
    }
    state.email = email;
    els.confirmEmail.textContent = email;
    renderPieces(els.confirmPieces);
    show('confirm', { history: 'push' });
  });

  els.fix.addEventListener('click', function () {
    els.confirmEmail.textContent = '';
    show('handover', { history: 'replace' });
  });

  function setBusy(busy) {
    if (els.yes) { els.yes.disabled = busy; els.yes.textContent = busy ? 'Sending…' : 'Yes, send'; }
    if (els.fix) els.fix.disabled = busy;
  }

  // {token, email, consent, pieces:[{handle, variantId}]} (#417). With one piece the old top-level handle and
  // variantId go too, so a send works against a worker that only knows the one-piece shape.
  function requestBody() {
    var body = {
      token: token,
      email: state.email,
      consent: !!els.consent.checked,
      pieces: state.pieces.map(function (p) { return { handle: p.handle, variantId: p.variantId || null }; })
    };
    if (state.pieces.length === 1) {
      body.handle = state.pieces[0].handle;
      body.variantId = state.pieces[0].variantId || null;
    }
    return body;
  }

  els.yes.addEventListener('click', function () {
    if (state.sending || !state.pieces.length || !state.email) return;
    var at = epoch;
    state.sending = true;
    setBusy(true);
    setStatus('');
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, SEND_TIMEOUT_MS) : null;
    var init = {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody())
    };
    if (controller) init.signal = controller.signal;
    fetch(PROXY, init).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        return { status: res.status, body: data };
      });
    }).catch(function () {
      return { status: 0, body: null };
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      if (at !== epoch) return; // the page was reset while this was in flight
      state.sending = false;
      setBusy(false);
      handleResponse(r);
    });
  });

  // The title of the piece a 422 names in detail (several pieces), or a generic "This piece".
  function namedPiece(body) {
    var hit = null;
    if (body && body.detail) state.pieces.forEach(function (p) { if (!hit && p.handle === body.detail) hit = p; });
    return hit ? '“' + hit.title + '”' : '';
  }

  function failingPiece(body) {
    return namedPiece(body) || (state.pieces.length > 1 ? 'One of these pieces' : 'This piece');
  }

  function handleResponse(r) {
    var body = r.body || {};
    if (r.status === 200) {
      // Saved but not emailed comes back as a 200 with emailed:false, never a 5xx: Shopify's app proxy replaces
      // any 5xx from the worker with a bare 500 and drops the body.
      if (body.saved === true && body.emailed === false && !body.duplicate) {
        track('saved_not_emailed');
        done(false);
        return;
      }
      track(body.duplicate ? 'duplicate' : 'sent');
      done(true);
      return;
    }
    // Errors keep the form filled, so a retry is one tap.
    if (r.status === 400) { track('bad_email'); show('handover', { history: 'replace' }); setStatus('Check the email address'); return; }
    if (r.status === 401) { track('unauthorised'); setStatus(NO_TOKEN); return; }
    if (r.status === 429) { track('rate_limited'); setStatus('Too many in a row, wait a minute'); return; }
    if (r.status === 422 && body.error === 'bad_variant') { track('bad_variant'); setStatus(namedPiece(body) ? 'That size of ' + namedPiece(body) + ' isn’t on the website. Pick it again' : 'That size isn’t on the website. Pick the piece again'); return; }
    if (r.status === 422) { track('not_online'); setStatus(failingPiece(body) + ' isn’t on the website'); return; }
    if (r.status === 0) { track('offline'); setStatus('No signal. Try again'); return; }
    // Any 5xx arrives as a bare 500 (see above), so one message covers a Shopify or storefront hiccup and a worker
    // that is not set up: a retry fixes the first, Andy the second.
    track('error');
    setStatus('Something went wrong. Try again, or ask Andy.');
  }

  /* ------- Done ------- */
  function done(emailed) {
    var email = state.email;
    els.confirmEmail.textContent = '';
    els.email.value = '';
    els.consent.checked = false;
    state.email = '';
    renderPieces(els.donePieces);
    if (emailed) {
      els.doneTitle.textContent = 'Thanks very much';
      els.doneText.textContent = 'Sent to ' + email + '. Check your inbox (and junk).';
    } else {
      els.doneTitle.textContent = 'Saved';
      els.doneText.textContent = 'Saved to your wishlist but the email didn’t go. Sign in at the site with this address.';
    }
    show('done', { history: 'replace' });
  }

  els.next.addEventListener('click', function () { reset(); });

  // Anna changes the piece. On the size step: drop the product being picked. On the hand-over: drop the piece
  // picked last. Either way back to Find with any other picked pieces kept; with none left it is a clean start.
  $$('[data-send-restart]').forEach(function (b) {
    b.addEventListener('click', function () {
      if (state.step === 'handover') state.pieces.pop();
      if (!state.pieces.length) { reset(); return; }
      epoch++;
      clearCurrent();
      clearFind();
      show('find', { history: 'replace' });
    });
  });

  // Another piece for the same customer: back to Find, the picked pieces kept (up to MAX_PIECES).
  if (els.addAnother) els.addAnother.addEventListener('click', function () {
    if (state.pieces.length >= MAX_PIECES) return;
    wipeCustomer();
    clearCurrent();
    clearFind();
    show('find', { history: 'push' });
  });

  /* ------- Search input ------- */
  els.search.addEventListener('input', function () {
    clearTimeout(searchTimer);
    // Typing makes any search or category list still on its way stale, so it can neither replace what is typed
    // for nor move focus off the search box.
    searchSeq++;
    var q = els.search.value.trim();
    searchTimer = setTimeout(function () { search(q); }, SEARCH_DEBOUNCE_MS);
  });

  /* ------- Start ------- */
  reset();
})();
